import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { FaArrowLeft, FaPlus, FaSyncAlt } from 'react-icons/fa';
import axiosInstance from '../../utility/axiosInstance';
import { getActionSelectStyles } from '../../utility/selectStyles';
import type { Database } from '../../store/slices/databasesSlice';
import { haMemberCount } from '../HaOption';
import TableList from './TableList';
import ResultGrid from './ResultGrid';
import SqlBox from './SqlBox';
import DataUnavailable from './DataUnavailable';
import RowFormModal, { WriteState } from './RowFormModal';
import NewTableModal from './NewTableModal';
import ImportModal, { ImportEnd } from './ImportModal';
import { AddColumnModal, RenameModal, DeleteColumnModal } from './ColumnModals';
import { useConfirm } from '../ConfirmContext';
import { exportFailureLine, exportProgress, runExport } from './chunks';
import { Delimiter } from './csv';
import { LoadedCsv, readCsvFile } from './csvFile';
import { ImportApi, runImportUpload } from './importUpload';
import {
    BAD_ANSWER, DataFailure, DataResult, DataTable, FIND_DELAY_MS, FIND_MAX_CHARS, GridSort, PAGE_SIZE, REQUEST_TIMEOUT_MS, UNADDRESSABLE_TABLE,
    cellUrl, classifyDataError, columnTypes, defaultFindColumn, haReadNote, infoUrl, kindLabel, nextPage, nextSort, pageUrl, readExpertMode, readPage,
    readResult, readTables, retryable, rowUnavailable, rowsDeleteUrl, rowsLabel, rowsUrl, searchUrl, tableKey, tableLabel, writeExpertMode,
} from './dataView';
import { ForeignKey, KeyedPage, TableInfo, cellEditKind, cellUpdate, deleteRowsConfirm, foreignKeyOf, isCut, readInfo, relatedValues, rowKey } from './rowForm';
import { RelatedPickerModal, RelatedRowModal } from './RelatedRows';
import RowSecurityModal from './RowSecurityModal';
import {
    CHECKING, SAVED, WriteOp, WriteOutcome, importFailedLine, importedLine, newWriteRef, readWriteFailure, readWriteSuccess,
    settleUrl, settleWrite, settledLine,
} from './writeErrors';
import tabStyles from '../../styles/InstanceDetailsModal.module.css';
import '../../styles/DatabaseDataView.scss';

export type DatabaseDataResource = Pick<Database, 'id' | 'name' | 'status' | 'db_name' | 'db_version'
    | 'ha' | 'member_ips' | 'data_view' | 'data_export' | 'data_related' | 'data_edit' | 'data_edit_locked'>;

interface DatabaseDataViewProps {
    db: DatabaseDataResource;
    onBack: () => void;
    /** Re-reads the databases list, so a row that changed meanwhile (ready, or its data view set up) is current. */
    onRefreshDatabase?: () => void;
}

/** One tab in version 1; this list is where a second one (logs, later) joins. The tab row shows once there are two. */
const TABS: ReadonlyArray<{ id: string; label: string }> = [{ id: 'data', label: 'Data' }];
const ACTIVE_TAB = 'data';
const SHOW_TABS = TABS.length > 1;

type TablesState = { status: 'loading' | 'ready' | 'failed'; list: DataTable[]; failure: DataFailure | null };
type RowsState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; result: DataResult & { key?: string[]; cut?: [number, number][] } }
    | { status: 'failed'; failure: DataFailure };
type Selection = { key: string; schema: string; name: string };
/** The open row form: Add row, or Edit row of one row by its key, with its cut cells loading or locked. */
type FormState =
    | { mode: 'add' }
    | { mode: 'edit'; key: string[]; originals: Record<string, string | null>; loading: ReadonlySet<string>; locked: ReadonlyMap<string, string> };
/** How a write ended, for the modal or the rows header. */
type WriteEnd = { saved: boolean; settled: boolean; line: string; changed?: boolean; unknown?: boolean; postgres?: string };

const sleep = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });
const IDLE: WriteState = { busy: null, line: null };

/** localStorage, or null where reaching it throws (some browsers do, with site data blocked). */
const browserStorage = (): Storage | null => {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

/**
 * A database's data view (DESIGN_V1.md D13), shown in place of the databases list: its tables with a filter, a table's
 * rows 50 at a time, and, with the Expert mode checkbox checked, one read-only SQL statement at a time.
 *
 * - No Redux and no browser storage for anything read here (plan 2a: rows are never stored). Component state only,
 *   and every request guards against a late answer for a table or page that is no longer the one shown. The one
 *   thing kept in browser storage is the Expert mode checkbox, a preference.
 * - Every call goes through axiosInstance with a 30 s timeout and never an Idempotency-Key header.
 * - One helper serves the whole database, so a failure that means "this database cannot be read right now" from ANY
 *   call replaces the whole view with the unavailable state; a refusal of one request stays in its own pane.
 */
const DatabaseDataView: React.FC<DatabaseDataViewProps> = ({ db, onBack, onRefreshDatabase }) => {
    // Known from the row: not ready, or no data view on it. Nothing is sent then.
    const blocked = rowUnavailable(db);
    const isBlocked = blocked !== null;

    const [reloadKey, setReloadKey] = useState(0);        // Try again: the list and the open page
    const [tablesReload, setTablesReload] = useState(0);  // the list alone
    const [rowsReload, setRowsReload] = useState(0);      // the open page alone
    const [infoReload, setInfoReload] = useState(0);      // the open table's columns, after a column change
    const [unavailable, setUnavailable] = useState<DataFailure | null>(null);
    const [tables, setTables] = useState<TablesState>({ status: 'loading', list: [], failure: null });
    const [selection, setSelection] = useState<Selection | null>(null);
    // The offsets of the pages before this one, so Previous is exact: a page the helper cut for size is short.
    const [page, setPage] = useState<{ offset: number; previous: number[] }>({ offset: 0, previous: [] });
    const [rows, setRows] = useState<RowsState>({ status: 'idle' });
    // Held here, not in the SQL box, so the text survives the unavailable state, Try again and unchecking Expert mode.
    const [sql, setSql] = useState('');
    const [expert, setExpert] = useState(() => readExpertMode(browserStorage()));
    const chooseExpert = (on: boolean) => {
        setExpert(on);
        writeExpertMode(browserStorage(), on);
    };
    // Sort and find (DESIGN_EDITING.md console flow 1), on a database whose helper has the 1.2.0 reads (data_export).
    const reads12 = db.data_export === true;
    const [sort, setSort] = useState<GridSort | null>(null);
    const [findColumn, setFindColumn] = useState<string | null>(null);
    const [findText, setFindText] = useState('');
    const [activeFind, setActiveFind] = useState<{ column: string; text: string } | null>(null);
    const findTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => { if (findTimer.current) clearTimeout(findTimer.current); }, []);
    // Export (DESIGN_EDITING.md console flow 10): the button's own label while it runs, and one line if it fails.
    const [exporting, setExporting] = useState<string | null>(null);
    const [exportLine, setExportLine] = useState<string | null>(null);
    // Editing (DESIGN_EDITING.md console flows 4 to 6 and 11), on HA too once its members carry the writer.
    // E28: Lock editing, per database and for the whole account; the server refuses every write while it is on
    const [locked, setLocked] = useState(db.data_edit_locked === true);
    const [lockBusy, setLockBusy] = useState(false);
    const [lockLine, setLockLine] = useState<string | null>(null);
    useEffect(() => { setLocked(db.data_edit_locked === true); }, [db.data_edit_locked]);
    const canEditMachine = db.data_edit === true;   // on HA too, when every member carries the writer (the backend's marker)
    const editOn = canEditMachine && !locked;
    // E27: related rows, for viewers and editors alike, on a build that names foreign keys and answers lookup
    const relatedOn = db.data_related === true;
    const [related, setRelated] = useState<{ fk: ForeignKey; values: string[] } | null>(null);
    const [picker, setPicker] = useState<{ fk: ForeignKey; fill: (value: string) => void } | null>(null);
    const [rlsOpen, setRlsOpen] = useState(false);   // E30: the table's row-level security, read only
    const [info, setInfo] = useState<{ key: string; info: TableInfo } | null>(null);
    const [form, setForm] = useState<FormState | null>(null);
    const [write, setWrite] = useState<WriteState>(IDLE);
    const [checked, setChecked] = useState<ReadonlySet<number>>(new Set());
    const [deleting, setDeleting] = useState<string | null>(null);
    // Import (DESIGN_EDITING.md console flow 9): a chosen file opens the modal; the hidden input lets it be re-picked.
    const [importFile, setImportFile] = useState<LoadedCsv | null>(null);
    const importInputRef = useRef<HTMLInputElement | null>(null);
    // "Saved." (or a delete's line) in the rows header, until the next action
    const [rowsLine, setRowsLine] = useState<string | null>(null);
    // A read after a write keeps the grid on screen while it runs
    const keepGrid = useRef(false);
    const { confirm } = useConfirm();
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    useEffect(() => {
        if (isBlocked) return;
        let active = true;
        setTables((t) => ({ ...t, status: 'loading', failure: null }));
        axiosInstance.get(`/v1/databases/${db.id}/data/tables`, { timeout: REQUEST_TIMEOUT_MS })
            .then((res) => {
                if (!active) return;
                const list = readTables(res.data);
                if (!list) {
                    setUnavailable(BAD_ANSWER);
                    setTables((t) => ({ ...t, status: 'failed', failure: BAD_ANSWER }));
                    return;
                }
                setTables({ status: 'ready', list, failure: null });
                // A table that is gone from a fresh list is no longer selected.
                setSelection((s) => (s && list.some((t) => tableKey(t) === s.key) ? s : null));
            })
            .catch((err) => {
                if (!active) return;
                const failure = classifyDataError(err);
                if (failure.kind === 'unavailable') setUnavailable(failure);
                setTables((t) => ({ ...t, status: 'failed', failure }));
            });
        return () => { active = false; };
    }, [db.id, isBlocked, reloadKey, tablesReload]);

    useEffect(() => {
        if (isBlocked || !selection) { setRows({ status: 'idle' }); return; }
        const url = reads12
            ? (activeFind ? searchUrl : pageUrl)(db.id, selection.schema, selection.name)
            : rowsUrl(db.id, selection.schema, selection.name);
        if (!url) { setRows({ status: 'failed', failure: UNADDRESSABLE_TABLE }); return; }
        let active = true;
        if (!keepGrid.current) setRows({ status: 'loading' });
        keepGrid.current = false;
        setChecked(new Set());
        const sorted = sort ? { sort: sort.column, dir: sort.dir } : {};
        const call = !reads12
            ? axiosInstance.get(url, { params: { limit: PAGE_SIZE, offset: page.offset }, timeout: REQUEST_TIMEOUT_MS })
            : activeFind
                ? axiosInstance.post(url, { limit: PAGE_SIZE, offset: page.offset, ...sorted, filter: { column: activeFind.column, contains: activeFind.text } }, { timeout: REQUEST_TIMEOUT_MS })
                : axiosInstance.get(url, { params: { limit: PAGE_SIZE, offset: page.offset, ...sorted }, timeout: REQUEST_TIMEOUT_MS });
        call
            .then((res) => {
                if (!active) return;
                const result = reads12 ? readPage(res.data) : readResult(res.data);
                if (!result) { setUnavailable(BAD_ANSWER); return; }
                setRows({ status: 'ready', result });
            })
            .catch((err) => {
                if (!active) return;
                const failure = classifyDataError(err);
                if (failure.kind === 'unavailable') { setUnavailable(failure); return; }
                setRows({ status: 'failed', failure });
            });
        return () => { active = false; };
    }, [db.id, isBlocked, selection?.key, page.offset, reloadKey, rowsReload, reads12, sort?.column, sort?.dir, activeFind?.column, activeFind?.text]);

    // The table's info (columns, key, what the app user may do): the edit controls follow it, and stay off without it.
    useEffect(() => {
        // a re-read of the same table keeps its controls on screen; another table starts without them
        setInfo((cur) => (cur && selection && cur.key === selection.key ? cur : null));
        if (isBlocked || !(editOn || relatedOn) || !selection) return;
        const url = infoUrl(db.id, selection.schema, selection.name);
        if (!url) return;
        let active = true;
        const key = selection.key;
        axiosInstance.get(url, { timeout: REQUEST_TIMEOUT_MS })
            .then((res) => {
                const read = readInfo(res.data);
                if (active && read) setInfo({ key, info: read });
            })
            .catch(() => undefined);   // the page's own read says what is wrong with the table
        return () => { active = false; };
    }, [db.id, isBlocked, editOn, relatedOn, selection?.key, reloadKey, infoReload]);

    const tryAgain = () => {
        setUnavailable(null);
        onRefreshDatabase?.();
        setReloadKey((k) => k + 1);
    };

    const selectTable = (t: DataTable) => {
        const key = tableKey(t);
        if (selection?.key === key) return;
        setRowsLine(null);
        setSelection({ key, schema: t.schema, name: t.name });
        setPage({ offset: 0, previous: [] });
        setExportLine(null);
        setSort(null);
        setFindColumn(null);
        setFindText('');
        setActiveFind(null);
        if (findTimer.current) { clearTimeout(findTimer.current); findTimer.current = null; }
    };

    /** A header click: the next sort, from the first page. */
    const sortBy = (column: string) => {
        setRowsLine(null);
        setSort((s) => nextSort(s, column));
        setPage({ offset: 0, previous: [] });
    };

    /** Find the text in the column, or, with the box empty, go back to the page. The grid stays up while it reads. */
    const applyFind = (rawText: string, column: string | null) => {
        if (findTimer.current) { clearTimeout(findTimer.current); findTimer.current = null; }
        const text = rawText.trim();
        const next = text && column ? { column, text: text.slice(0, FIND_MAX_CHARS) } : null;
        if (next?.column === activeFind?.column && next?.text === activeFind?.text) return;
        setRowsLine(null);
        if (rows.status === 'ready') keepGrid.current = true;
        setActiveFind(next);
        setPage({ offset: 0, previous: [] });
    };

    /** Typing: the find runs once the typing pauses. */
    const typeFind = (text: string) => {
        setFindText(text);
        if (findTimer.current) clearTimeout(findTimer.current);
        findTimer.current = setTimeout(() => applyFind(text, findOn), FIND_DELAY_MS);
    };

    /** The table, in chunks, into a file the browser saves (the SSHPage pattern: a client-built Blob and a[download]). */
    const startExport = async () => {
        if (!selection || exporting !== null) return;
        const target = selection;
        setRowsLine(null);
        setExportLine(null);
        setExporting(exportProgress(0, 1));
        const outcome = await runExport(
            (url, params) => axiosInstance.get(url, { params, timeout: REQUEST_TIMEOUT_MS }).then((res) => res.data),
            db.id, target.schema, target.name,
            (got, total) => { if (mounted.current) setExporting(exportProgress(got, total)); },
            () => !mounted.current,
        );
        if (!mounted.current) return;
        setExporting(null);
        if (!outcome.ok) { setExportLine(exportFailureLine(outcome)); return; }
        // Each part is a Uint8Array over its own ArrayBuffer (chunks.ts base64ToBytes), which a Blob takes as is.
        const blob = new Blob(outcome.parts as BlobPart[], { type: 'text/csv;charset=utf-8' });
        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = outcome.fileName;
        a.click();
        URL.revokeObjectURL(href);
    };

    // ── Import (console flow 9) ──────────────────────────────────────────────────────────────────────────────────
    // The staging routes: reserve, then one raw 1 MiB chunk at a time (application/octet-stream, not JSON).
    const importApi: ImportApi = {
        start: (importId, bytes, chunks) =>
            axiosInstance.post(`/v1/databases/${db.id}/data/imports`, { import_id: importId, bytes, chunks }, { timeout: REQUEST_TIMEOUT_MS }).then((r) => r.data),
        putChunk: (importId, seq, chunk) =>
            axiosInstance.put(`/v1/databases/${db.id}/data/imports/${importId}/chunks/${seq}`, chunk,
                { headers: { 'Content-Type': 'application/octet-stream' }, timeout: REQUEST_TIMEOUT_MS }).then((r) => r.data),
    };
    const uploadCsv = (bytes: Uint8Array, onProgress: (sent: number, total: number) => void, cancelled: () => boolean) =>
        runImportUpload(importApi, bytes, newWriteRef, onProgress, cancelled);

    /**
     * Commit a staged file into a table (target) or a new table (create), sent ONCE with its write_ref. An answer that
     * does not say is settled by that write_ref, never by sending the commit again (console flow 11).
     */
    const commitImport = async (
        importId: string, sha256: string, delimiter: Delimiter, columns: string[],
        where: { target: { table: string } } | { create: Record<string, unknown> },
    ): Promise<ImportEnd> => {
        const writeRef = newWriteRef();
        const url = `/v1/databases/${db.id}/data/imports/${encodeURIComponent(importId)}/commit`;
        try {
            const res = await axiosInstance.post(url, { write_ref: writeRef, sha256, delimiter, columns, ...where }, { timeout: REQUEST_TIMEOUT_MS });
            if (readWriteSuccess(res?.status, res?.data).kind === 'applied') {
                const rows = res?.data?.rows;
                return { ok: true, line: Number.isInteger(rows) ? importedLine(rows) : SAVED };
            }
        } catch (err: any) {
            const out = readWriteFailure(err, 'import_commit');
            if (out.kind === 'refused') {
                const e = err?.response?.data?.error;
                return { ok: false, line: importFailedLine(e?.code ?? '', e?.details), ...(out.postgres ? { postgres: out.postgres } : {}) };
            }
        }
        const settled = await settleWrite(
            (u) => axiosInstance.get(u, { timeout: REQUEST_TIMEOUT_MS }).then((r) => r.data), settleUrl(db.id, writeRef), sleep,
        );
        return { ok: settled === 'applied', line: settledLine(settled) };
    };

    const openImport = () => { setRowsLine(null); importInputRef.current?.click(); };
    const onImportFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        e.target.value = '';   // so the same file can be chosen again
        if (!f) return;
        const loaded = await readCsvFile(f);
        if (mounted.current) setImportFile(loaded);
    };

    const selectedTable = selection ? tables.list.find((t) => tableKey(t) === selection.key) ?? null : null;
    // The column Find looks in: the one chosen, else the table's first text column
    const findOn = findColumn ?? defaultFindColumn(selectedTable?.columns ?? []);
    const result = rows.status === 'ready' ? rows.result : null;
    const pager = result ? nextPage(page.offset, result) : null;
    // What editing may do on this page: the info says what the app user may do, the page says each row's key
    const tableInfo = info && selection && info.key === selection.key ? info.info : null;
    const keyed: KeyedPage | null = result && result.key && result.cut ? { ...result, key: result.key, cut: result.cut } : null;
    const hasKey = !!keyed && keyed.key.length > 0;
    const canInsert = editOn && !!tableInfo?.can.insert;
    const canUpdate = editOn && !!tableInfo?.can.update;   // a row opens only when rowKey names it
    const canDelete = editOn && !!tableInfo?.can.delete && hasKey;
    const canAlter = editOn && !!tableInfo?.can.alter;   // create/rename/add/drop: the table menu and column caret
    const keyColumns = new Set(keyed?.key ?? []);

    /** A read of the open page after a write, with the grid kept on screen. */
    const rereadPage = () => {
        keepGrid.current = true;
        setRowsReload((n) => n + 1);
    };

    // Table and column edits (DESIGN_EDITING.md console flows 2, 7, 8). Each is its own small modal, its state here.
    type TableModal =
        | { kind: 'new'; csv?: LoadedCsv }
        | { kind: 'add_column' }
        | { kind: 'rename_table' }
        | { kind: 'rename_column'; column: string }
        | { kind: 'delete_column'; column: string };
    const [tableModal, setTableModal] = useState<TableModal | null>(null);
    const [tableWrite, setTableWrite] = useState<WriteState>(IDLE);
    const tableBusy = (line: string) => setTableWrite({ busy: line, line: null });
    const reloadTables = () => { onRefreshDatabase?.(); setTablesReload((n) => n + 1); };
    const openTableModal = (m: TableModal) => { setRowsLine(null); setTableWrite(IDLE); setTableModal(m); };
    // The Tables pane's Import CSV: pick a file, then the New table modal opens drafted from it (flow 3)
    const newFromCsvRef = useRef<HTMLInputElement | null>(null);
    const onNewFromCsvChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        const loaded = await readCsvFile(f);
        if (mounted.current) openTableModal({ kind: 'new', csv: loaded });
    };

    /**
     * One table or column write, through the same handshake and settle as a row write. On success the modal closes
     * and the table list is read again; a refusal stays in the modal. `after` runs on success (select a new table).
     */
    const runTableWrite = async (op: WriteOp, call: (writeRef: string) => Promise<any>, busyLine: string, after?: () => void) => {
        tableBusy(busyLine);
        const end = await sendWrite(op, call, tableBusy);
        if (!mounted.current) return;
        if (end.saved) {
            setTableModal(null);
            setTableWrite(IDLE);
            reloadTables();
            after?.();
            return;
        }
        setTableWrite({ busy: null, line: end.line });
        // An unknown outcome is read on the next list, so the view never shows a stale table set
        if (end.settled || end.unknown) reloadTables();
    };

    const tableUrl = (schema: string, table: string) => rowsUrl(db.id, schema, table)?.slice(0, -'/rows'.length) ?? null;

    const createTable = (body: Record<string, unknown>) => {
        const name = String(body.table ?? '');
        void runTableWrite('create_table',
            (writeRef) => axiosInstance.post(`/v1/databases/${db.id}/data/tables`, { ...body, write_ref: writeRef }, { timeout: REQUEST_TIMEOUT_MS }),
            'Creating...', () => setSelection({ key: tableKey({ schema: 'public', name }), schema: 'public', name }));
    };
    const alterTable = (change: any, busyLine = 'Saving...') => {
        if (!selection) return;
        const url = tableUrl(selection.schema, selection.name);
        if (!url) return;
        const renamedTo = change.kind === 'rename_table' ? String(change.to) : null;
        void runTableWrite(change.kind,
            (writeRef) => axiosInstance.patch(url, { write_ref: writeRef, change }, { timeout: REQUEST_TIMEOUT_MS }),
            busyLine,
            () => {
                setRowsLine('Saved.');
                if (renamedTo) { setSelection({ key: tableKey({ schema: 'public', name: renamedTo }), schema: 'public', name: renamedTo }); return; }
                // a column added, renamed or deleted: the grid and the edit form read the table's columns again
                keepGrid.current = true;
                setRowsReload((n) => n + 1);
                setInfoReload((n) => n + 1);
            });
    };
    const deleteTable = async () => {
        if (!selection) return;
        const url = tableUrl(selection.schema, selection.name);
        if (!url) return;
        const name = selection.name;
        if (!(await confirm({ title: 'Delete table', message: `Delete ${name}?`, confirmText: 'Delete', cancelText: 'Cancel', type: 'danger' }))) return;
        const end = await sendWrite('drop_table',
            (writeRef) => axiosInstance.delete(url, { params: { write_ref: writeRef }, timeout: REQUEST_TIMEOUT_MS }), (line) => setRowsLine(line));
        if (!mounted.current) return;
        if (end.saved || end.settled || end.unknown) {
            if (end.saved) { setSelection(null); setRowsLine(null); }
            reloadTables();
        } else {
            setRowsLine(end.line);
        }
    };
    const restoreTable = async (trashName: string) => {
        const end = await sendWrite('restore_table',
            (writeRef) => axiosInstance.post(`/v1/databases/${db.id}/data/trash/${encodeURIComponent(trashName)}/restore`, { write_ref: writeRef }, { timeout: REQUEST_TIMEOUT_MS }),
            () => undefined);
        if (!mounted.current) return;
        if (end.saved || end.settled || end.unknown) reloadTables();
    };

    /**
     * One write, sent ONCE with a write_ref made for it. An answer that does not say (no answer, a timeout, a 202, a
     * 5xx) is asked about by that write_ref, never sent again (console flow 11).
     */
    const sendWrite = async (op: WriteOp, call: (writeRef: string) => Promise<any>, busy: (line: string) => void, onlyColumn?: string): Promise<WriteEnd> => {
        const writeRef = newWriteRef();
        let outcome: WriteOutcome;
        try {
            const res = await call(writeRef);
            outcome = readWriteSuccess(res?.status, res?.data);
        } catch (err) {
            outcome = readWriteFailure(err, op, onlyColumn);
        }
        if (outcome.kind === 'applied') return { saved: true, settled: false, line: SAVED };
        if (outcome.kind === 'refused') {
            return { saved: false, settled: false, line: outcome.line, changed: outcome.code === 'row_changed', ...(outcome.postgres ? { postgres: outcome.postgres } : {}) };
        }
        if (mounted.current) busy(CHECKING);
        const settled = await settleWrite(
            (url) => axiosInstance.get(url, { timeout: REQUEST_TIMEOUT_MS }).then((res) => res.data),
            settleUrl(db.id, writeRef), sleep,
        );
        return { saved: settled === 'applied', settled: true, line: settledLine(settled), ...(settled === 'unknown' ? { unknown: true } : {}) };
    };

    /** A form's write ended: saved closes it and reads the page again; anything else stays in it, the input kept. */
    const endFormWrite = (end: WriteEnd) => {
        if (!mounted.current) return;
        if (end.saved) {
            setForm(null);
            setWrite(IDLE);
            setRowsLine(SAVED);
            rereadPage();
            return;
        }
        // An outcome nobody can tell locks the form: sending it again could save it twice. Reload reads the page.
        setWrite({ busy: null, line: end.line, ...(end.changed ? { changed: true } : {}), ...(end.unknown ? { locked: true } : {}), ...(end.postgres ? { postgres: end.postgres } : {}) });
        if (end.settled) rereadPage();
    };
    const formBusy = (line: string) => setWrite({ busy: line, line: null });

    const openAdd = () => {
        setRowsLine(null);
        setWrite(IDLE);
        setForm({ mode: 'add' });
    };

    const addRow = async (columns: string[], row: (string | { default: true })[]) => {
        const url = selection ? rowsUrl(db.id, selection.schema, selection.name) : null;
        if (!url) return;
        formBusy('Saving...');
        endFormWrite(await sendWrite('insert',
            (writeRef) => axiosInstance.post(url, { write_ref: writeRef, columns, rows: [row] }, { timeout: REQUEST_TIMEOUT_MS }),
            formBusy, columns.length === 1 ? columns[0] : undefined));
    };

    /** Each cut cell of the row, whole, one at a time; a cell over 64 KiB stays read-only. */
    const loadCutCells = async (key: string[], columns: string[]) => {
        const url = selection ? cellUrl(db.id, selection.schema, selection.name) : null;
        for (const column of columns) {
            let value: string | null = null;
            let lockLine: string | null = null;
            try {
                if (!url) throw new Error('unaddressable');
                const res = await axiosInstance.post(url, { key, column }, { timeout: REQUEST_TIMEOUT_MS });
                const d = res.data;
                if (d && d.column === column && d.too_large === true) lockLine = 'Too large to edit here.';
                else if (d && d.column === column && (typeof d.value === 'string' || d.value === null)) value = d.value;
                else lockLine = 'Could not load.';
            } catch {
                lockLine = 'Could not load.';
            }
            if (!mounted.current) return;
            setForm((f) => {
                if (!f || f.mode !== 'edit' || f.key !== key) return f;   // another row is open now
                const loading = new Set(f.loading);
                loading.delete(column);
                if (lockLine !== null) return { ...f, loading, locked: new Map(f.locked).set(column, lockLine) };
                return { ...f, loading, originals: { ...f.originals, [column]: value } };
            });
        }
    };

    const openEdit = (r: number) => {
        if (!keyed || !canUpdate) return;
        const key = rowKey(keyed, r);
        if (!key) return;
        const originals: Record<string, string | null> = {};
        const cutColumns: string[] = [];
        keyed.columns.forEach((name, c) => {
            if (name in originals || cutColumns.includes(name)) return;
            if (isCut(keyed, r, c)) cutColumns.push(name);
            else originals[name] = keyed.rows[r][c];
        });
        setRowsLine(null);
        setWrite(IDLE);
        setForm({ mode: 'edit', key, originals, loading: new Set(cutColumns), locked: new Map() });
        if (cutColumns.length > 0) void loadCutCells(key, cutColumns);
    };

    const saveRow = async (changes: { column: string; old: string | null; new: string | null }[]) => {
        const url = selection ? rowsUrl(db.id, selection.schema, selection.name) : null;
        if (!url || !form || form.mode !== 'edit') return;
        const key = form.key;
        formBusy('Saving...');
        endFormWrite(await sendWrite('update',
            (writeRef) => axiosInstance.patch(url, { write_ref: writeRef, key, changes }, { timeout: REQUEST_TIMEOUT_MS }),
            formBusy, changes.length === 1 ? changes[0].column : undefined));
    };

    /**
     * E26: one cell edited in the grid, saved as Edit row saves it (one PATCH, settled if its answer is lost). True closes
     * the cell (saved, or an outcome that reads the page again); false keeps it open with what was typed.
     */
    const saveCell = async (r: number, c: number, text: string): Promise<boolean> => {
        const url = selection ? rowsUrl(db.id, selection.schema, selection.name) : null;
        const key = keyed ? rowKey(keyed, r) : null;
        if (!url || !keyed || !key || !tableInfo) return true;
        const column = keyed.columns[c];
        const built = cellUpdate(tableInfo, column, keyed.rows[r][c], text);
        if (!built.ok) { setRowsLine(built.line); return false; }
        if (built.changes.length === 0) return true;
        setRowsLine('Saving...');
        const end = await sendWrite('update',
            (writeRef) => axiosInstance.patch(url, { write_ref: writeRef, key, changes: built.changes }, { timeout: REQUEST_TIMEOUT_MS }),
            (line) => setRowsLine(line), column);
        if (!mounted.current) return true;
        setRowsLine(end.line);
        if (end.saved || end.changed || end.settled) { rereadPage(); return true; }
        return false;
    };

    /** E28: lock or unlock editing on this database; the server enforces it, this only follows. */
    const chooseLock = async (next: boolean) => {
        setLockBusy(true);
        setLockLine(null);
        // The box follows the click at once; a refusal puts it back. The server's answer is what counts either way.
        setLocked(next);
        if (next) { setForm(null); setChecked(new Set()); }
        setRowsLine(null);
        try {
            await axiosInstance.put(`/v1/databases/${db.id}/data/lock`, { locked: next }, { timeout: REQUEST_TIMEOUT_MS });
            if (mounted.current) onRefreshDatabase?.();
        } catch {
            if (!mounted.current) return;
            setLocked(!next);
            setLockLine(next ? 'Not locked. Try again.' : 'Not unlocked. Try again.');
        } finally {
            if (mounted.current) setLockBusy(false);
        }
    };

    // E27: the grid's foreign-key columns, the Related row an arrow opens, and Open table
    const relatedColumns = relatedOn && tableInfo && result
        ? new Set(result.columns.map((n, i) => (foreignKeyOf(tableInfo, n) ? i : -1)).filter((i) => i >= 0))
        : null;
    const openRelated = (r: number, c: number) => {
        if (!tableInfo || !result) return;
        const fk = foreignKeyOf(tableInfo, result.columns[c]);
        const named = fk ? relatedValues(fk, { columns: result.columns, rows: result.rows, cut: keyed?.cut ?? [] }, r) : null;
        if (!fk || !named) { setRowsLine('This row names no related row.'); return; }
        setRelated({ fk, values: named.values });
    };
    const openRelatedTable = (fk: ForeignKey, values: string[]) => {
        setRelated(null);
        const t = tables.list.find((x) => x.schema === fk.ref_schema && x.name === fk.ref_table);
        if (!t) { setRowsLine(`${fk.ref_table} is not in the table list.`); return; }
        selectTable(t);
        // Find on the referenced column, so the related row is on the first page
        setFindColumn(fk.ref_columns[0]);
        setFindText(values[0]);
        setActiveFind({ column: fk.ref_columns[0], text: values[0].slice(0, FIND_MAX_CHARS) });
    };
    /** A single-column foreign key's field in Add or Edit row can Pick its value from the referenced table. */
    const pickable = (column: string) => {
        const fk = relatedOn ? foreignKeyOf(tableInfo, column) : null;
        return !!fk && fk.columns.length === 1;
    };

    const deleteFromForm = async () => {
        const url = selection ? rowsDeleteUrl(db.id, selection.schema, selection.name) : null;
        if (!url || !form || form.mode !== 'edit') return;
        const key = form.key;
        if (!(await confirm(deleteRowsConfirm(1)))) return;
        formBusy('Deleting...');
        endFormWrite(await sendWrite('delete',
            (writeRef) => axiosInstance.post(url, { write_ref: writeRef, keys: [key] }, { timeout: REQUEST_TIMEOUT_MS }), formBusy));
    };

    /** Changed elsewhere: the form closes and the page is read again, so the row is opened from what it holds now. */
    const reloadChanged = () => {
        setForm(null);
        setWrite(IDLE);
        rereadPage();
    };

    const toggleRow = (r: number) => {
        setRowsLine(null);
        setChecked((c) => {
            const next = new Set(c);
            if (next.has(r)) next.delete(r); else next.add(r);
            return next;
        });
    };
    const toggleAll = (on: boolean) => {
        setRowsLine(null);
        setChecked(on && keyed ? new Set(keyed.rows.map((_, r) => r).filter((r) => rowKey(keyed, r) !== null)) : new Set());
    };

    const deleteChecked = async () => {
        const url = selection ? rowsDeleteUrl(db.id, selection.schema, selection.name) : null;
        if (!url || !keyed || deleting !== null) return;
        const keys = Array.from(checked).sort((a, b) => a - b).map((r) => rowKey(keyed, r)).filter((k): k is string[] => k !== null);
        if (keys.length === 0) return;
        setRowsLine(null);
        if (!(await confirm(deleteRowsConfirm(keys.length)))) return;
        setDeleting('Deleting...');
        const end = await sendWrite('delete',
            (writeRef) => axiosInstance.post(url, { write_ref: writeRef, keys }, { timeout: REQUEST_TIMEOUT_MS }),
            (line) => setDeleting(line));
        if (!mounted.current) return;
        setDeleting(null);
        setRowsLine(end.line);
        if (end.saved || end.settled) rereadPage();
    };

    const goNext = () => {
        if (!pager?.hasNext) return;
        setRowsLine(null);
        const next = pager.nextOffset;
        setPage((p) => ({ offset: next, previous: [...p.previous, p.offset] }));
    };
    const goPrevious = () => {
        setRowsLine(null);
        setPage((p) => (p.previous.length === 0 ? p : {
            offset: p.previous[p.previous.length - 1],
            previous: p.previous.slice(0, -1),
        }));
    };

    const members = db.ha ? haMemberCount(db.member_ips) : null;
    const haNote = haReadNote(db);
    const kind = selectedTable ? kindLabel(selectedTable.kind) : null;
    // How the rows are changed, said where the rows are (a row click and a tick have no button of their own)
    const editHint = !selection || !editOn || !tableInfo ? null
        : canUpdate && keyed && canDelete ? 'Double-click a cell to edit it, or click the row. Tick rows to delete.'
        : canUpdate && keyed ? 'Double-click a cell to edit it, or click the row.'
        : canDelete ? 'Tick rows to delete.'
        : canInsert ? null
        : 'Read-only.';
    const lockHint = canEditMachine && locked && selection ? 'Editing is locked.' : null;

    const rowsPane = (
        <div className="db-data-pane db-data-rows">
            <div className="db-data-pane-header">
                <span className="db-data-pane-title">{selection ? tableLabel(selection) : 'Rows'}</span>
                {kind && <span className="db-data-pane-sub">{kind}</span>}
                {(lockHint ?? editHint) && <span className="db-data-pane-sub db-data-edit-hint">{lockHint ?? editHint}</span>}
                {tableInfo?.rls?.enabled && (
                    <button type="button" className="db-data-rls-chip" onClick={() => setRlsOpen(true)} title="Row-level security">
                        RLS on{tableInfo.rls.policies.length ? ` · ${tableInfo.rls.policies.length}` : ''}
                    </button>
                )}
                {selection && (
                    <div className="db-data-pane-actions">
                        {rowsLine && <span className="db-data-rows-line" role="status">{rowsLine}</span>}
                        {canDelete && checked.size > 0 && (
                            <button type="button" className="db-data-delete-button" onClick={deleteChecked} disabled={deleting !== null}>
                                {deleting ?? `Delete ${checked.size}`}
                            </button>
                        )}
                        {canInsert && (
                            <button type="button" className="db-data-text-button db-data-create-button" onClick={openAdd} disabled={deleting !== null}>
                                <FaPlus aria-hidden="true" /> Add row
                            </button>
                        )}
                        {canInsert && (
                            <button type="button" className="db-data-text-button" onClick={openImport} disabled={deleting !== null}>Import</button>
                        )}
                        {canAlter && (
                            <div className="db-data-table-menu">
                                <Select
                                    aria-label="Table menu"
                                    placeholder="Table"
                                    options={[{ value: 'add_column', label: 'Add column' }, { value: 'rename_table', label: 'Rename table' }, { value: 'delete_table', label: 'Delete table' }]}
                                    value={null}
                                    isSearchable={false}
                                    onChange={(o: any) => {
                                        if (o?.value === 'add_column') openTableModal({ kind: 'add_column' });
                                        else if (o?.value === 'rename_table') openTableModal({ kind: 'rename_table' });
                                        else if (o?.value === 'delete_table') void deleteTable();
                                    }}
                                    styles={getActionSelectStyles()}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                        )}
                        {db.data_export === true && (
                            <button type="button" className="db-data-text-button" onClick={startExport} disabled={exporting !== null}>
                                {exporting ?? 'Export'}
                            </button>
                        )}
                        <button
                            type="button"
                            className="db-data-icon-button"
                            onClick={() => setRowsReload((n) => n + 1)}
                            disabled={rows.status === 'loading'}
                            aria-label="Read this page again"
                            title="Read this page again"
                        >
                            <FaSyncAlt />
                        </button>
                    </div>
                )}
            </div>
            {exportLine && <p className="db-data-export-line" role="status">{exportLine}</p>}
            {reads12 && selection && (
                <div className="db-data-find">
                    <div className="db-data-find-column">
                        <Select
                            aria-label="Find in column"
                            placeholder="Column"
                            options={(selectedTable?.columns ?? []).map((c) => ({ value: c.name, label: c.name }))}
                            value={findOn ? { value: findOn, label: findOn } : null}
                            onChange={(o: any) => {
                                const column = o?.value ?? null;
                                setFindColumn(column);
                                applyFind(findText, column);
                            }}
                            styles={getActionSelectStyles()}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                    <input
                        type="search"
                        className="db-data-find-text"
                        aria-label="Find"
                        placeholder="Find"
                        maxLength={FIND_MAX_CHARS}
                        value={findText}
                        onChange={(e) => {
                            if (e.target.value) typeFind(e.target.value);
                            else { setFindText(''); applyFind('', findOn); }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') applyFind(findText, findOn); }}
                    />
                </div>
            )}
            {!selection ? (
                <div className="empty-state db-data-empty-state">Select a table.</div>
            ) : rows.status === 'loading' || rows.status === 'idle' ? (
                <div className="loading-state"><FaSyncAlt className="spin" /> Loading rows...</div>
            ) : rows.status === 'failed' ? (
                <div className="db-data-error" role="alert">
                    <p className="db-data-error-message" title={rows.failure.apiMessage}>{rows.failure.message}</p>
                    {retryable(rows.failure) && (
                        <button type="button" className="btn btn-secondary" onClick={() => setRowsReload((n) => n + 1)}>Retry</button>
                    )}
                </div>
            ) : (
                <>
                    <ResultGrid
                        label={`Rows of ${tableLabel(selection)}`}
                        columns={rows.result.columns}
                        rows={rows.result.rows}
                        types={expert ? columnTypes(rows.result.columns, selectedTable?.columns ?? []) : undefined}
                        emptyText={page.offset > 0 ? 'No more rows.' : 'No rows.'}
                        truncatedText={null}
                        {...(reads12 ? { onSort: sortBy, sort } : {})}
                        {...(canUpdate && keyed ? { onRowClick: openEdit, rowClickable: (r: number) => rowKey(keyed, r) !== null } : {})}
                        {...(relatedColumns && relatedColumns.size > 0 ? { related: { columns: relatedColumns, open: openRelated } } : {})}
                        {...(canUpdate && keyed && tableInfo ? { cellEdit: {
                            kind: (r: number, c: number) => cellEditKind(tableInfo, keyed, r, c),
                            nullable: (c: number) => tableInfo.columns.some((x) => x.name === keyed.columns[c] && !x.not_null),
                            onSave: saveCell,
                        } } : {})}
                        {...(canDelete && keyed ? { selection: {
                            checked, selectable: (r: number) => rowKey(keyed, r) !== null, onToggle: toggleRow, onToggleAll: toggleAll,
                        } } : {})}
                        {...(canAlter ? { columnMenu: {
                            onRename: (column: string) => openTableModal({ kind: 'rename_column', column }),
                            onDelete: (column: string) => openTableModal({ kind: 'delete_column', column }),
                            canDelete: (column: string) => !keyColumns.has(column),
                        } } : {})}
                    />
                    <div className="db-data-pager">
                        <button type="button" onClick={goPrevious} disabled={page.previous.length === 0}>← Previous</button>
                        <span className="db-data-pager-label">{rowsLabel(page.offset, rows.result.rows.length)}</span>
                        <button type="button" onClick={goNext} disabled={!pager?.hasNext}>Next →</button>
                    </div>
                </>
            )}
            {form && tableInfo && (
                <RowFormModal
                    mode={form.mode}
                    info={tableInfo}
                    expert={expert}
                    write={write}
                    {...(form.mode === 'edit'
                        ? { originals: form.originals, loading: form.loading, locked: form.locked, onSave: saveRow, onReload: reloadChanged,
                            ...(canDelete ? { onDelete: deleteFromForm } : {}) }
                        : { onAdd: addRow, onReload: reloadChanged })}
                    onClose={() => { setForm(null); setWrite(IDLE); }}
                    pickable={pickable}
                    onPick={(column, fill) => {
                        const fk = foreignKeyOf(tableInfo, column);
                        if (fk) setPicker({ fk, fill });
                    }}
                />
            )}
            {rlsOpen && tableInfo?.rls && selection && (
                <RowSecurityModal table={tableLabel(selection)} rls={tableInfo.rls} onClose={() => setRlsOpen(false)} />
            )}
            {related && (
                <RelatedRowModal databaseId={db.id} fk={related.fk} values={related.values} onOpenTable={openRelatedTable} onClose={() => setRelated(null)} />
            )}
            {picker && (
                <RelatedPickerModal
                    databaseId={db.id}
                    fk={picker.fk}
                    refTable={tables.list.find((x) => x.schema === picker.fk.ref_schema && x.name === picker.fk.ref_table) ?? null}
                    onPick={(value) => { picker.fill(value); setPicker(null); }}
                    onClose={() => setPicker(null)}
                />
            )}
            {editOn && (
                <input
                    ref={newFromCsvRef}
                    type="file"
                    accept=".csv,text/csv,text/plain"
                    style={{ display: 'none' }}
                    aria-hidden="true"
                    tabIndex={-1}
                    onChange={onNewFromCsvChosen}
                />
            )}
            {canInsert && (
                <input
                    ref={importInputRef}
                    type="file"
                    accept=".csv,text/csv,text/plain"
                    style={{ display: 'none' }}
                    aria-hidden="true"
                    tabIndex={-1}
                    onChange={onImportFileChosen}
                />
            )}
            {importFile && selection && (
                <ImportModal
                    table={selection.name}
                    tableColumns={tableInfo ? tableInfo.columns.map((c) => c.name) : (selectedTable?.columns ?? []).map((c) => c.name)}
                    file={importFile}
                    expert={expert}
                    upload={uploadCsv}
                    commit={(importId, sha, delim, cols) => commitImport(importId, sha, delim, cols, { target: { table: selection.name } })}
                    onSucceeded={() => { rereadPage(); reloadTables(); }}
                    onClose={() => setImportFile(null)}
                />
            )}
            {tableModal?.kind === 'new' && (
                <NewTableModal
                    expert={expert}
                    busy={tableWrite.busy}
                    line={tableWrite.line}
                    onCreate={createTable}
                    onClose={() => { setTableModal(null); setTableWrite(IDLE); }}
                    {...(tableModal.csv ? { initialCsv: tableModal.csv } : {})}
                    importCsv={{
                        upload: uploadCsv,
                        commit: (importId, sha, delim, cols, create) => commitImport(importId, sha, delim, cols, { create }),
                        onSucceeded: (tableName) => {
                            setTableModal(null); setTableWrite(IDLE);
                            reloadTables();
                            setSelection({ key: tableKey({ schema: 'public', name: tableName }), schema: 'public', name: tableName });
                        },
                    }}
                />
            )}
            {tableModal?.kind === 'add_column' && tableInfo && (
                <AddColumnModal expert={expert} existing={tableInfo.columns.map((c) => c.name)} busy={tableWrite.busy} line={tableWrite.line}
                    onAdd={(change) => alterTable(change)} onClose={() => { setTableModal(null); setTableWrite(IDLE); }} />
            )}
            {tableModal?.kind === 'rename_table' && selection && (
                <RenameModal title="Rename table" current={selection.name} existing={tables.list.filter((t) => t.schema === 'public').map((t) => t.name)} busy={tableWrite.busy} line={tableWrite.line}
                    onRename={(to) => alterTable({ kind: 'rename_table', to })} onClose={() => { setTableModal(null); setTableWrite(IDLE); }} />
            )}
            {tableModal?.kind === 'rename_column' && tableInfo && (
                <RenameModal title="Rename column" current={tableModal.column} existing={tableInfo.columns.map((c) => c.name)} busy={tableWrite.busy} line={tableWrite.line}
                    onRename={(to) => alterTable({ kind: 'rename_column', column: tableModal.column, to })} onClose={() => { setTableModal(null); setTableWrite(IDLE); }} />
            )}
            {tableModal?.kind === 'delete_column' && (
                <DeleteColumnModal column={tableModal.column} busy={tableWrite.busy} line={tableWrite.line}
                    onDelete={() => alterTable({ kind: 'drop_column', column: tableModal.column }, 'Deleting...')} onClose={() => { setTableModal(null); setTableWrite(IDLE); }} />
            )}
        </div>
    );

    return (
        <div className="db-data-view">
            <button type="button" className="back-button" onClick={onBack}>
                <FaArrowLeft /> Back to databases
            </button>

            <div className="db-data-header">
                <h2 className="db-data-title">
                    {db.name}
                    {db.ha && <span className="db-ha-badge">{members ? `HA · ${members} machines` : 'HA'}</span>}
                </h2>
                <p className="db-data-subtitle">
                    PostgreSQL {db.db_version} · {db.db_name}{editOn ? '' : ' · read-only'}
                </p>
            </div>

            {SHOW_TABS && (
                <div className={tabStyles.tabHeader} role="tablist" aria-label="Database views">
                    {TABS.map((t) => (
                        <button
                            type="button"
                            key={t.id}
                            id={`db-data-tab-${t.id}`}
                            role="tab"
                            aria-selected={ACTIVE_TAB === t.id}
                            aria-controls={`db-data-panel-${t.id}`}
                            className={ACTIVE_TAB === t.id ? tabStyles.activeTab : ''}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            )}

            <div id="db-data-panel-data" {...(SHOW_TABS ? { role: 'tabpanel', 'aria-labelledby': 'db-data-tab-data' } : {})}>
                {blocked ? (
                    <DataUnavailable reason={blocked.message} onRetry={tryAgain} />
                ) : unavailable ? (
                    <DataUnavailable reason={unavailable.message} detail={unavailable.apiMessage} onRetry={tryAgain} />
                ) : (
                    <>
                        <div className="db-data-switches">
                            <label className="db-data-expert">
                                <input type="checkbox" checked={expert} onChange={(e) => chooseExpert(e.target.checked)} />
                                Expert mode
                            </label>
                            {canEditMachine && (
                                <label className="db-data-expert" title="Locked, nothing on this database can be changed from here or the API until it is unlocked">
                                    <input type="checkbox" checked={locked} disabled={lockBusy} onChange={(e) => void chooseLock(e.target.checked)} />
                                    Lock editing
                                </label>
                            )}
                            {lockLine && <span className="db-data-lock-line" role="status">{lockLine}</span>}
                        </div>
                        {haNote && <p className="db-data-ha-note">{haNote}</p>}
                        <div className="db-data-layout">
                            <TableList
                                tables={tables.list}
                                status={tables.status}
                                failure={tables.failure}
                                selectedKey={selection?.key ?? null}
                                onSelect={selectTable}
                                onRefresh={() => setTablesReload((n) => n + 1)}
                                {...(editOn ? { onNewTable: () => openTableModal({ kind: 'new' }), onImportCsv: () => newFromCsvRef.current?.click(), onRestore: (name: string) => void restoreTable(name) } : {})}
                            />
                            {rowsPane}
                        </div>
                        {expert && (
                            <SqlBox databaseId={db.id} sql={sql} onSqlChange={setSql} onUnavailable={setUnavailable} storage={browserStorage()}
                                canWrite={editOn} onWrote={() => { reloadTables(); rereadPage(); }} />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DatabaseDataView;
